/**
 * Acciones para editar clientes en la gestión de clientes
 * gc_ec_ = Gestión de Clientes (gc) _ Editar Cliente (ec)
 */
import {Dispatch} from 'react'
import * as http from '../../../api/http'
import {ENDPOINTS} from '../../../api/config'
import {State} from '../../../reducers'
import {
  generalOpenModalError,
  generalOpenModalMessage,
  generalShowLoader,
} from '../../general.actions'
import {GESTION_CLIENTES_TYPES} from '../../../constants/gestionCliente.types'
import {GestionClienteAction} from '../../../interfaces/gestion-cliente.interface'
import {KEYS} from '../../../constants/keysStorage'
import {cargarDistritosDelCliente, getMapaDistritoMunicipio, getMunicipios, setDistritos, setMunicipios} from '../../catalogos.actions'
import {bloqueaPorDistrito, MENSAJE_DISTRITO_BLOQUEA_V2, MENSAJE_DISTRITO_REQUERIDO, municipioQueCorrigeElDistrito} from '../../../utils/normativa.utils'
import {MENSAJE_CORREO_RECEPTOR_INVALIDO, validarCorreosReceptor} from '../../../utils/correo.utils'

// ******************************************************************************************
// ? ACTION CREATORS
// ******************************************************************************************
export const gc_ec_FormHandleChange = (form: any) => ({
  type: GESTION_CLIENTES_TYPES.EC_FORM_HANDLE_CHANGE,
  payload: form,
})

export const gc_ec_FormAddError = (errors: any) => ({
  type: GESTION_CLIENTES_TYPES.EC_FORM_ADD_ERROR,
  payload: errors,
})

export const gc_ec_SetClienteSeleccionado = (cliente: any) => ({
  type: GESTION_CLIENTES_TYPES.EC_SET_CLIENTE_SELECCIONADO,
  payload: cliente,
})

export const gc_ec_Clear = () => ({
  type: GESTION_CLIENTES_TYPES.EC_CLEAR,
  payload: null,
})

// ******************************************************************************************
// ? FUNCIONES O PROCEDIMIENTOS SIN PETICIONES HTTP
// ******************************************************************************************
export const gc_ec_HandleFormChange =
  (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
  async (dispatch: Dispatch<any>, getState: () => State) => {
    let {name, value} = e.target

    if (name === 'tipoDocumento') {
      const docObj = getState().catalogos.tipoDocumento.find((d: any) => d.codigo === value)
      dispatch(gc_ec_FormHandleChange({tipoDocumento: value, nombreTipoDocumento: docObj?.valor ?? ''}))
      return
    }

    if (name === 'departamento') {
      const depto = getState().catalogos.departamentos.find((d: any) => value === d.codigo)
      dispatch(gc_ec_FormHandleChange({departamento: value, nombreDepartamento: depto?.valor ?? ''}))
      dispatch(gc_ec_FormHandleChange({municipio: '', nombreMunicipio: '', distrito: ''}))
      dispatch(setDistritos([]))
      if (depto) {
        await dispatch(getMunicipios(Number(value), depto.valor, true))
      } else {
        dispatch(setMunicipios([]))
      }
      if (value) {
        await dispatch(cargarDistritosDelCliente(value, '', false) as any)
        dispatch(getMapaDistritoMunicipio(value) as any)
      }
      return
    }

    if (name === 'municipio') {
      dispatch(gc_ec_FormHandleChange({municipio: value, distrito: ''}))
      const departamento = getState().gestionCliente.editarCliente.formCliente?.departamento
      if (departamento) await dispatch(cargarDistritosDelCliente(departamento, value, true) as any)
      else dispatch(setDistritos([]))
      return
    }

    if (name === 'distrito') {
      dispatch(gc_ec_FormHandleChange({distrito: value}))
      dispatch(gc_ec_FormAddError({distrito: ''}))
      const formulario = getState().gestionCliente.editarCliente.formCliente
      const municipioDerivado = municipioQueCorrigeElDistrito(
        getState().catalogos.mapaDistritoMunicipio,
        getState().catalogos.municipios,
        formulario?.departamento,
        value,
        formulario?.municipio
      )
      if (municipioDerivado) {
        const municipioEncontrado = getState().catalogos.municipios.find(
          (mun: any) => `${mun?.codigo ?? ''}`.trim() === municipioDerivado
        )
        dispatch(gc_ec_FormHandleChange({municipio: municipioDerivado, nombreMunicipio: municipioEncontrado?.valor ?? ''}))
      }
      return
    }

    if (name === 'actividad') {
      const actividadObj = getState().catalogos.actividadesEconomicas.find(
        (a: any) => a.codigo === value
      )
      dispatch(gc_ec_FormHandleChange({actividad: value, descripcion: actividadObj?.valor ?? ''}))
      return
    }

    dispatch(gc_ec_FormHandleChange({[name]: value}))
  }

export const gc_ec_OpenEditar =
  (cliente: any) => async (dispatch: Dispatch<any>, getState: () => State) => {
    dispatch(gc_ec_SetClienteSeleccionado(cliente))
    if (cliente?.departamento) {
      const cachedMunis = getState().catalogos.municipiosPorDepartamento[cliente.departamento]
      if (cachedMunis) {
        dispatch(setMunicipios(cachedMunis))
      } else {
        const depto = getState().catalogos.departamentos.find(
          (d: any) => cliente.departamento === d.codigo
        )
        if (depto) {
          await dispatch(getMunicipios(Number(cliente.departamento), depto.valor, false))
        }
      }
      await dispatch(cargarDistritosDelCliente(cliente.departamento, cliente?.municipio, false) as any)
      dispatch(getMapaDistritoMunicipio(cliente.departamento) as any)
    }

    // Cargar correos adicionales desde la DB para poblar los chips
    if (cliente?.idcliente) {
      try {
        const codigoempresa = localStorage.getItem(KEYS.CODIGO_EMPRESA)
        const username = getState().userLogin.datos.username
        const res: any = await http.get(
          `${ENDPOINTS.GESTION_CLIENTES_GET_CLIENTE_BY_ID}?codigoempresa=${codigoempresa}&username=${username}&idcliente=${cliente.idcliente}`
        )
        const data = res?.data
        if (data) {
          const correosAdicionales: string[] = Array.isArray(data.correos_adicionales)
            ? data.correos_adicionales
            : []
          const emailRaw: string = data.email ?? cliente.email ?? ''
          const emailsSplit: string[] = emailRaw
            ? emailRaw.split(',').map((e: string) => e.trim()).filter((e: string) => e)
            : []
          const todosCorreos = [...emailsSplit, ...correosAdicionales]
          dispatch(gc_ec_FormHandleChange({chipsEmail: todosCorreos, email: ''}))
        }
      } catch {
        // Si falla, dejar los emails del campo email como chips iniciales
        const emailRaw = cliente.email ?? ''
        const chips = emailRaw
          ? emailRaw.split(',').map((e: string) => e.trim()).filter((e: string) => e)
          : []
        dispatch(gc_ec_FormHandleChange({chipsEmail: chips, email: ''}))
      }
    }
  }

// ******************************************************************************************
// ? PETICIONES HTTP
// ******************************************************************************************
export const gc_ec_UpdateClienteConfirm =
  (onSuccess?: () => void) => async (dispatch: Dispatch<any>, getState: () => State) => {
    if (getState().general.loader) return
    dispatch(generalShowLoader(true))

    const formCliente = getState().gestionCliente.editarCliente.formCliente
    const codigoempresa = localStorage.getItem(KEYS.CODIGO_EMPRESA)
    const id_user = localStorage.getItem(KEYS.ID_CLIENT)

    if (!formCliente?.idcliente) {
      dispatch(generalShowLoader(false))
      return dispatch(generalOpenModalError('No se ha seleccionado un cliente'))
    }

    if (formCliente?.departamento && bloqueaPorDistrito(getState().catalogos.datosEmisor, formCliente?.distrito)) {
      dispatch(generalShowLoader(false))
      dispatch(gc_ec_FormAddError({distrito: MENSAJE_DISTRITO_REQUERIDO}))
      return dispatch(generalOpenModalError(MENSAJE_DISTRITO_BLOQUEA_V2))
    }

    // Consolidar todos los correos: chips + email escrito en el input (aún no confirmado)
    const allEmails = [...(formCliente.chipsEmail ?? [])]
    const emailInput = formCliente.email?.trim()
    if (emailInput) {
      const parsed = emailInput.split(',').map((e: string) => e.trim()).filter((e: string) => e)
      parsed.forEach((e: string) => { if (!allEmails.includes(e)) allEmails.push(e) })
    }

    const correoInvalido = validarCorreosReceptor(formCliente.email, formCliente.chipsEmail)
    if (correoInvalido) {
      dispatch(generalShowLoader(false))
      dispatch(gc_ec_FormAddError({email: correoInvalido}))
      return dispatch(generalOpenModalError(`${correoInvalido} ${MENSAJE_CORREO_RECEPTOR_INVALIDO}`))
    }

    const body = {
      iddatoscliente: formCliente.idcliente,
      tipodocumento: formCliente.tipoDocumento,
      numdocumento: formCliente.numId,
      nombre: formCliente.nombre,
      departamento: formCliente.departamento,
      municipio: formCliente.municipio,
      ...(formCliente.distrito ? {distrito: formCliente.distrito} : {}),
      direccion: formCliente.direccion,
      email: allEmails[0] ?? '',
      correos_adicionales: allEmails.slice(1),
      actualizar_correos: true,
      telefono: formCliente.telefono,
      nrc: formCliente.nrc,
      codactividad: formCliente.actividad,
      descactividad: formCliente.descripcion,
      codigoempresa: codigoempresa,
      usuariomodificacion: id_user,
      correocc: formCliente.correocc ?? '',
    }

    await http
      .post(ENDPOINTS.GESTION_CLIENTES_UPDATE_CLIENTE, body)
      .then(async (res: any) => {
        await dispatch(
          generalOpenModalMessage(
            'Cliente Actualizado',
            res?.data?.message ?? 'El cliente ha sido actualizado exitosamente'
          )
        )
        dispatch(gc_ec_Clear())
        dispatch(generalShowLoader(false))
        onSuccess?.()
      })
      .catch(async (err: any) => {
        const internalMsg = err?.response?.data?.internalMessage ?? ''
        const isDuplicate = internalMsg.toLowerCase().includes('ya existe un cliente')
        const displayMsg = isDuplicate
          ? 'Ya existe un cliente con ese número de documento'
          : err?.response?.data?.message ?? 'No se pudo actualizar el cliente'
        await dispatch(generalOpenModalError(displayMsg, err?.response?.data?.status))
      })
      .finally(() => dispatch(generalShowLoader(false)))
  }
